/**
 * Jubly Calendar Day View — Google Calendar style
 * Single day timeline with drag-to-create and drag-to-move
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 60;
const SNAP_MINUTES = 15;

export default function DayView({
  currentDate,
  events,
  calendars,
  onEventClick,
  onTimeSlotClick,
  onEventUpdate,
  onCreateEventRange
}) {
  const scrollRef = useRef(null);
  const colRef = useRef(null);
  const [dragCreate, setDragCreate] = useState(null);
  const [dragMove, setDragMove] = useState(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 8 * HOUR_HEIGHT;
  }, [currentDate]);

  const today = new Date();
  const isToday = currentDate.getDate() === today.getDate() &&
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear();

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  const yToHour = useCallback((y) => {
    const raw = y / HOUR_HEIGHT;
    return Math.max(0, Math.min(24, Math.round(raw * (60 / SNAP_MINUTES)) / (60 / SNAP_MINUTES)));
  }, []);

  const hourToTime = (hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${dh}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const dayEvents = useMemo(() => {
    const dayStart = new Date(currentDate); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(currentDate); dayEnd.setHours(23, 59, 59, 999);
    return events.filter(event => {
      const s = new Date(event.start_time);
      const e = new Date(event.end_time);
      return s <= dayEnd && e >= dayStart;
    }).map(event => {
      const s = new Date(event.start_time);
      const e = new Date(event.end_time);
      let startH = s.getHours() + s.getMinutes() / 60;
      let endH = e.getHours() + e.getMinutes() / 60;
      if (s < dayStart) startH = 0;
      if (e > dayEnd) endH = 24;
      return { ...event, startH, endH, top: startH * HOUR_HEIGHT, height: Math.max((endH - startH) * HOUR_HEIGHT, 22), isAllDay: event.is_all_day || (endH - startH) >= 24 };
    }).sort((a, b) => a.startH - b.startH || b.height - a.height);
  }, [currentDate, events]);

  const timedEvents = useMemo(() => {
    const timed = dayEvents.filter(e => !e.isAllDay);
    const columns = [];
    for (const ev of timed) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        if (ev.startH >= columns[c][columns[c].length - 1].endH) { columns[c].push(ev); ev._col = c; placed = true; break; }
      }
      if (!placed) { ev._col = columns.length; columns.push([ev]); }
    }
    const totalCols = columns.length || 1;
    for (const ev of timed) ev._totalCols = totalCols;
    return timed;
  }, [dayEvents]);

  const allDayEvents = dayEvents.filter(e => e.isAllDay);
  const getColor = (event) => event.calendar_color || event.color || '#3B82F6';
  const lighten = (hex, a = 0.88) => {
    const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.round(r + (255 - r) * a)}, ${Math.round(g + (255 - g) * a)}, ${Math.round(b + (255 - b) * a)})`;
  };

  // Drag-to-create
  const handleMouseDown = (e) => {
    if (e.button !== 0 || dragMove) return;
    const rect = colRef.current?.getBoundingClientRect();
    if (!rect) return;
    const y = e.clientY - rect.top;
    setDragCreate({ startY: y, currentY: y });
  };

  const handleMouseMove = useCallback((e) => {
    if (dragCreate) {
      const rect = colRef.current?.getBoundingClientRect();
      if (rect) setDragCreate(prev => ({ ...prev, currentY: e.clientY - rect.top }));
    }
    if (dragMove) {
      const rect = colRef.current?.getBoundingClientRect();
      if (rect) {
        const y = e.clientY - rect.top - dragMove.offsetY;
        setDragMove(prev => ({ ...prev, currentTop: yToHour(y) * HOUR_HEIGHT }));
      }
    }
  }, [dragCreate, dragMove, yToHour]);

  const handleMouseUp = useCallback(() => {
    if (dragCreate) {
      const startH = yToHour(Math.min(dragCreate.startY, dragCreate.currentY));
      const endH = yToHour(Math.max(dragCreate.startY, dragCreate.currentY));
      if (endH - startH >= 0.25) {
        const s = new Date(currentDate); s.setHours(Math.floor(startH), Math.round((startH % 1) * 60), 0, 0);
        const e = new Date(currentDate); e.setHours(Math.floor(endH), Math.round((endH % 1) * 60), 0, 0);
        onCreateEventRange?.({ startDate: s, endDate: e });
      }
      setDragCreate(null);
    }
    if (dragMove) {
      const newH = yToHour(dragMove.currentTop);
      const orig = new Date(dragMove.event.start_time);
      const dur = new Date(dragMove.event.end_time) - orig;
      const ns = new Date(currentDate); ns.setHours(Math.floor(newH), Math.round((newH % 1) * 60), 0, 0);
      if (ns.getTime() !== orig.getTime()) {
        onEventUpdate?.(dragMove.event.id, { startTime: ns.toISOString(), endTime: new Date(ns.getTime() + dur).toISOString() });
      }
      setDragMove(null);
    }
  }, [dragCreate, dragMove, currentDate, yToHour, onCreateEventRange, onEventUpdate]);

  useEffect(() => {
    if (dragCreate || dragMove) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }
  }, [dragCreate, dragMove, handleMouseMove, handleMouseUp]);

  const createRect = dragCreate ? (() => {
    const sH = yToHour(Math.min(dragCreate.startY, dragCreate.currentY));
    const eH = yToHour(Math.max(dragCreate.startY, dragCreate.currentY));
    return { top: sH * HOUR_HEIGHT, height: (eH - sH) * HOUR_HEIGHT, label: `${hourToTime(sH)} – ${hourToTime(eH)}` };
  })() : null;

  return (
    <div className="flex flex-col h-full bg-white select-none">
      <div className="flex border-b border-gray-200 flex-shrink-0">
        <div className="w-[56px] flex-shrink-0" />
        <div className="flex-1 py-2 text-center">
          <div className={`text-[11px] font-medium tracking-wider ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
            {currentDate.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
          </div>
          <div className={`text-[26px] font-normal leading-tight mt-0.5 ${isToday ? 'w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto' : 'text-gray-800'}`}>
            {currentDate.getDate()}
          </div>
        </div>
      </div>

      {allDayEvents.length > 0 && (
        <div className="flex border-b border-gray-200 flex-shrink-0">
          <div className="w-[56px] flex-shrink-0 text-[10px] text-gray-500 text-right pr-2 pt-2">all-day</div>
          <div className="flex-1 px-2 py-1 space-y-1">
            {allDayEvents.map(event => (
              <button key={event.id} onClick={() => onEventClick?.(event)}
                className="w-full text-left text-sm px-3 py-1.5 rounded text-white font-medium truncate hover:opacity-90"
                style={{ backgroundColor: getColor(event) }}>{event.title}</button>
            ))}
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="flex relative" style={{ minHeight: HOURS.length * HOUR_HEIGHT }}>
          <div className="w-[56px] flex-shrink-0">
            {HOURS.map(hour => (
              <div key={hour} className="text-[10px] text-gray-500 text-right pr-2 relative" style={{ height: HOUR_HEIGHT }}>
                {hour > 0 && <span className="absolute -top-[6px] right-2">{formatHour(hour)}</span>}
              </div>
            ))}
          </div>

          <div ref={colRef} className={`flex-1 relative border-l border-gray-100 ${isToday ? 'bg-blue-50/20' : ''}`}
            style={{ cursor: dragCreate ? 'ns-resize' : 'default' }}
            onMouseDown={handleMouseDown}>
            {HOURS.map(hour => (
              <div key={hour} className="border-b border-gray-100" style={{ height: HOUR_HEIGHT }} />
            ))}

            {createRect && (
              <div className="absolute left-1 right-1 bg-blue-200/60 border border-blue-400 rounded-md z-30 pointer-events-none"
                style={{ top: createRect.top, height: Math.max(createRect.height, 15) }}>
                <span className="text-[10px] text-blue-700 font-medium px-1">{createRect.label}</span>
              </div>
            )}

            {timedEvents.map(event => {
              const color = getColor(event);
              const colW = 100 / event._totalCols;
              const left = event._col * colW;
              const isDragging = dragMove?.event.id === event.id;
              const top = isDragging ? dragMove.currentTop : event.top;
              return (
                <div key={event.id}
                  className={`absolute rounded-md px-2 py-1 text-left overflow-hidden border-l-[3px] cursor-grab ${isDragging ? 'shadow-lg opacity-80 z-40' : 'hover:shadow-md z-10'}`}
                  style={{ top, height: event.height, left: `calc(${left}% + 2px)`, width: `calc(${colW}% - 4px)`, backgroundColor: lighten(color), borderLeftColor: color }}
                  onMouseDown={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setDragMove({ event, offsetY: e.clientY - r.top, currentTop: event.top }); }}
                  onClick={(e) => { if (!dragMove) { e.stopPropagation(); onEventClick?.(event); } }}>
                  <div className="text-xs font-medium truncate" style={{ color }}>{event.title}</div>
                  {event.height > 36 && <div className="text-[10px] text-gray-500">{new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} – {new Date(event.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</div>}
                </div>
              );
            })}

            {isToday && (
              <div className="absolute left-0 right-0 z-20 pointer-events-none"
                style={{ top: (today.getHours() + today.getMinutes() / 60) * HOUR_HEIGHT }}>
                <div className="absolute -left-[5px] -top-[5px] w-[10px] h-[10px] bg-red-500 rounded-full" />
                <div className="border-t-2 border-red-500" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
